import React from 'react';
import { PageHeader, Badge, Stat } from './shared';
import { ModalHeader, ModalShell } from './Modals';
import { Icon } from './ui/icons';

function cx(...classes: (string | undefined | false)[]): string {
  return classes.filter(Boolean).join(' ');
}

function Avatar({ size = 32 }: { size?: number }) {
  return (
    <div 
      className="rounded-full bg-gradient-to-br from-orange-400 to-purple-600 flex items-center justify-center text-white font-bold"
      style={{ width: size, height: size, fontSize: size * 0.4 }}
    >
      A
    </div>
  );
}

// Evidence + Audit log + Time machine + Evidence packet modal.
// The auditor-facing surface.

// ── Types and Interfaces ─────────────────────────────────────────────────────

interface AuditTarget {
  type: string;
  id: string;
}

interface AuditEvent {
  id: string;
  kind: string;
  actor: string;
  summary: string;
  at: string;
  target?: AuditTarget;
  diff?: Record<string, [string, string]>;
}

interface EvidenceRun {
  id: string;
  control: string;
  verdict: 'passing' | 'failing' | 'pending';
  repo: string;
  commitSha: string;
  summary: string;
  interpreter: string;
  at: string;
  evidenceHash: string;
}

interface Framework {
  id: string;
  name: string;
  requirements: any[];
}

interface AuditLogData {
  auditLog?: {
    events: AuditEvent[];
  };
  evidence?: {
    runs: EvidenceRun[];
  };
  conformance?: {
    rules: any[];
  };
}

interface AuditLogProps {
  data: AuditLogData;
  navigate: (route: string) => void;
}

interface AuditEventRowProps {
  e: AuditEvent;
  navigate: (route: string) => void;
  compact?: boolean;
}

interface RecentActivityProps {
  data: AuditLogData;
  navigate: (route: string) => void;
}

interface EvidenceTrailProps {
  controlId: string;
  data: AuditLogData;
  onExport: () => void;
}

interface TimeMachineProps {
  asOf: string;
  onChange: (date: string) => void;
  max?: string;
}

interface EvidencePacketModalProps {
  open: boolean;
  onClose: () => void;
  framework: Framework | null;
  data: AuditLogData;
  asOf: string;
}

interface PacketOptionProps {
  checked: boolean;
  onToggle: () => void;
  title: string;
  sub: string;
}

// ── Utility Functions ────────────────────────────────────────────────────────

export function timeAgo(iso: string): string {
  const t = new Date(iso).getTime();
  const diff = Date.now() - t;
  const m = Math.round(diff / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

// ── Constants ────────────────────────────────────────────────────────────────

const KIND_ICON = {
  'control.edit': { icon: 'shield', color: '#7F56D9' },
  'control.publish': { icon: 'shield', color: '#159D4C' },
  'control.run': { icon: 'shield', color: '#0A69DA' },
  'rule.create': { icon: 'git-branch', color: '#7F56D9' },
  'rule.edit': { icon: 'git-branch', color: '#CA9401' },
  'interpreter.edit': { icon: 'git-branch', color: '#CA9401' },
  'normalizer.edit': { icon: 'book', color: '#CA9401' },
  'acceptance.create': { icon: 'alert-triangle', color: '#FA500E' },
  'acceptance.approve': { icon: 'check', color: '#159D4C' },
  'review.request': { icon: 'users', color: '#CA9401' },
  'tag.create': { icon: 'tag', color: '#FA500E' },
  'framework.add': { icon: 'shield', color: '#0A69DA' },
  'member.invite': { icon: 'users', color: '#7F56D9' },
  'token.create': { icon: 'settings', color: '#0A69DA' },
  'repo.install': { icon: 'book', color: '#FA500E' },
};

// ── Components ───────────────────────────────────────────────────────────────

export function AuditEventRow({ e, navigate, compact = false }: AuditEventRowProps) {
  const k = KIND_ICON[e.kind as keyof typeof KIND_ICON] || { icon: 'dots', color: '#6B7280' };
  return (
    <button
      type="button"
      onClick={() => {
        if (!e.target) return;
        if (e.target.type === 'control') navigate(`controls/${e.target.id}`);
        if (e.target.type === 'framework') navigate(`frameworks/${e.target.id}`);
        if (e.target.type === 'rule') navigate('conformance');
        if (e.target.type === 'interpreter') navigate('interpreters');
        if (e.target.type === 'normalizer') navigate('normalizers');
        if (e.target.type === 'acceptance') navigate('risk-acceptances');
        if (e.target.type === 'tag') navigate(`tags/${e.target.id}`);
        if (e.target.type === 'repo') navigate(`repositories/${e.target.id}`);
        if (e.target.type === 'pr') {
          const [repo, num] = e.target.id.split('/');
          navigate(`repositories/${repo}/prs/${num}`);
        }
      }}
      className={cx(
        'flex items-start gap-3 hover:bg-[#FCF8F1] cursor-pointer text-left',
        compact ? 'px-4 py-2.5' : 'px-5 py-3.5'
      )}
    >
      <span
        className="h-7 w-7 rounded-full grid place-items-center shrink-0 mt-0.5"
        style={{ background: `${k.color}1a` }}
      >
        <Icon name={k.icon as any} size={13} stroke={k.color} />
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          {e.actor === 'ci-bot' ? (
            <span className="font-data text-[11px] font-bold px-1.5 py-[1px] rounded-[3px] bg-[#EEEFF2] text-ink/65">
              ci-bot
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5">
              <Avatar name={e.actor} size={16} />
              <span className="font-display font-bold text-ink text-[12.5px]">{e.actor}</span>
            </span>
          )}
          <span className="font-data text-[11px] text-ink/45">·</span>
          <span
            className="font-data text-[10.5px] uppercase tracking-[0.04em] px-1.5 py-[1px] rounded-[3px]"
            style={{ background: `${k.color}1a`, color: k.color }}
          >
            {e.kind}
          </span>
        </div>
        <div className="font-display text-[13px] text-ink/85 mt-1">{e.summary}</div>
        {e.diff && !compact && (
          <pre className="mt-1.5 font-data text-[11px] text-ink/55 leading-snug">
            {Object.entries(e.diff)
              .map(([k, [a, b]]) => `${k}: ${a} → ${b}`)
              .join('\n')}
          </pre>
        )}
      </div>
      <span className="font-data text-[11.5px] text-ink/45 shrink-0">{timeAgo(e.at)}</span>
    </button>
  );
}

export default function AuditLog({ data, navigate }: AuditLogProps) {
  const [actorFilter, setActorFilter] = React.useState('all');
  const [kindFilter, setKindFilter] = React.useState('all');
  const events = data.auditLog?.events || [];
  const actors = [...new Set(events.map((e) => e.actor))];
  const kinds = [...new Set(events.map((e) => e.kind))];
  const list = events.filter((e) => {
    if (actorFilter !== 'all' && e.actor !== actorFilter) return false;
    if (kindFilter !== 'all' && e.kind !== kindFilter) return false;
    return true;
  });

  return (
    <div className="flex flex-col gap-8">
      <PageHeader
        title="Audit log"
        subtitle="Every change to controls, rules, acceptances, members, tokens. Append-only — your evidence that controls were governed correctly."
        right={
          <button
            type="button"
            onClick={() =>
              (window as any).notify?.('Audit log CSV', {
                sub: 'Building filtered export…',
                tone: 'info',
              })
            }
            className="h-10 px-4 rounded-[10px] border border-[#F3E4C8] bg-white font-display font-bold text-[14px] inline-flex items-center gap-2 hover:border-ink/40 shrink-0"
          >
            <Icon name="download" size={14} stroke="#121111" /> Export CSV
          </button>
        }
      />

      <div className="grid grid-cols-4 gap-4">
        <Stat label="Events" value={events.length} />
        <Stat
          label="Today"
          value={
            events.filter(
              (e) => new Date(e.at).toDateString() === new Date('2026-05-19').toDateString()
            ).length
          }
          tone="info"
        />
        <Stat label="Distinct actors" value={actors.length} />
        <Stat label="Distinct kinds" value={kinds.length} />
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-data text-[12px] text-ink/55">Actor:</span>
        <button
          type="button"
          onClick={() => setActorFilter('all')}
          className={cx(
            'h-8 px-3 rounded-[8px] font-display text-[12.5px]',
            actorFilter === 'all'
              ? 'bg-ink text-white font-semibold'
              : 'text-ink/70 hover:bg-[#F7EDDB]'
          )}
        >
          All
        </button>
        {actors.map((a) => (
          <button
            type="button"
            key={a}
            onClick={() => setActorFilter(a)}
            className={cx(
              'h-8 px-3 rounded-[8px] font-display text-[12.5px] inline-flex items-center gap-1.5',
              actorFilter === a
                ? 'bg-ink text-white font-semibold'
                : 'text-ink/70 hover:bg-[#F7EDDB]'
            )}
          >
            {a === 'ci-bot' ? (
              <Icon name="settings" size={11} stroke={actorFilter === a ? 'white' : '#121111'} />
            ) : (
              <Avatar name={a} size={14} />
            )}
            {a}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-data text-[12px] text-ink/55">Kind:</span>
        <button
          type="button"
          onClick={() => setKindFilter('all')}
          className={cx(
            'h-8 px-3 rounded-[8px] font-display text-[12.5px]',
            kindFilter === 'all'
              ? 'bg-ink text-white font-semibold'
              : 'text-ink/70 hover:bg-[#F7EDDB]'
          )}
        >
          All
        </button>
        {kinds.map((k) => {
          const info = KIND_ICON[k as keyof typeof KIND_ICON] || { color: '#6B7280' };
          return (
            <button
              type="button"
              key={k}
              onClick={() => setKindFilter(k)}
              className={cx(
                'h-8 px-3 rounded-[8px] font-display text-[12.5px] inline-flex items-center gap-1.5',
                kindFilter === k
                  ? 'bg-ink text-white font-semibold'
                  : 'text-ink/70 hover:bg-[#F7EDDB]'
              )}
            >
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: info.color }} />
              {k}
            </button>
          );
        })}
      </div>

      <ul className="rounded-[10px] border border-[#F3E4C8] bg-white divide-y divide-[#F7EDDB]">
        {list.map((e) => (
          <AuditEventRow key={e.id} e={e} navigate={navigate} />
        ))}
        {!list.length && (
          <li className="px-5 py-10 text-center font-data text-[13px] text-ink/45">
            No events match these filters.
          </li>
        )}
      </ul>
    </div>
  );
}

// ── RecentActivity — compact card for the Dashboard ────────────────────────

export function RecentActivity({ data, navigate }: RecentActivityProps) {
  const events = (data.auditLog?.events || []).slice(0, 6);
  return (
    <div className="rounded-[10px] border border-[#F3E4C8] bg-white">
      <div className="px-5 py-3 border-b border-[#F7EDDB] flex items-center justify-between bg-[#FCF8F1]/60">
        <span className="font-display font-bold text-[11px] uppercase tracking-[0.04em] text-ink/60 inline-flex items-center gap-2">
          <Icon name="calendar" size={14} stroke="#121111" />
          Recent activity
        </span>
        <button
          type="button"
          onClick={() => navigate('audit-log')}
          className="font-display text-[13px] font-semibold text-[#FA500E] hover:underline inline-flex items-center gap-1"
        >
          View audit log <Icon name="arrow-up-right" size={12} stroke="currentColor" />
        </button>
      </div>
      <ul className="divide-y divide-[#F7EDDB]">
        {events.map((e) => (
          <AuditEventRow key={e.id} e={e} navigate={navigate} compact />
        ))}
      </ul>
    </div>
  );
}

// ── EvidenceTrail — per-control run history ────────────────────────────────

export function EvidenceTrail({ controlId, data, onExport }: EvidenceTrailProps) {
  const runs = (data.evidence?.runs || [])
    .filter((r) => r.control === controlId)
    .sort((a, b) => b.at.localeCompare(a.at));
  
  const verdictFg = (v: string) => (v === 'passing' ? '#159D4C' : v === 'failing' ? '#F40B0B' : '#CA9401');
  
  return (
    <div className="rounded-[14px] border border-[rgba(18,17,17,0.2)] p-7 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-display font-bold text-ink text-[16px]">Evidence trail</div>
          <div className="font-data text-[12px] text-ink/55 mt-0.5">
            Append-only history of every run · hash-anchored to the analyzer report
          </div>
        </div>
        <button
          type="button"
          onClick={onExport}
          className="h-9 px-3 rounded-[8px] border border-[#F3E4C8] hover:bg-[#FCF8F1] font-display font-semibold text-[13px] inline-flex items-center gap-1.5"
        >
          <Icon name="download" size={13} stroke="#121111" /> Export packet
        </button>
      </div>
      {runs.length === 0 ? (
        <div className="rounded-[8px] border border-dashed border-[#F3E4C8] bg-[#FCF8F1] p-4 font-display text-[13px] text-ink/55">
          No runs yet for this control.
        </div>
      ) : (
        <div className="rounded-[8px] border border-[#F3E4C8] overflow-hidden">
          <div
            className="grid items-center px-4 h-9 border-b border-[#F7EDDB] bg-[#FCF8F1] font-display text-[10px] font-bold uppercase tracking-[0.04em] text-ink/55"
            style={{ gridTemplateColumns: '100px 1fr 1.4fr 1fr 130px 110px' }}
          >
            <span>Verdict</span>
            <span>Repo · commit</span>
            <span>Summary</span>
            <span>Interpreter</span>
            <span>When</span>
            <span>Evidence hash</span>
          </div>
          <ul>
            {runs.map((r) => (
              <li
                key={r.id}
                className="grid items-center px-4 py-2.5 border-t border-[#F7EDDB] first:border-0 hover:bg-[#FCF8F1]"
                style={{
                  gridTemplateColumns: '100px 1fr 1.4fr 1fr 130px 110px',
                }}
              >
                <span className="inline-flex items-center gap-1.5">
                  <span
                    className="h-2 w-2 rounded-full"
                    style={{ background: verdictFg(r.verdict) }}
                  />
                  <span
                    className="font-display font-bold text-[12px]"
                    style={{ color: verdictFg(r.verdict) }}
                  >
                    {r.verdict}
                  </span>
                </span>
                <div className="min-w-0">
                  <div className="font-display font-bold text-ink text-[12.5px] truncate">
                    {r.repo}
                  </div>
                  <div className="font-data text-[11px] text-ink/55">{r.commitSha}</div>
                </div>
                <span className="font-data text-[12px] text-ink/75 truncate">{r.summary}</span>
                <span className="font-data text-[11.5px] text-ink/65 truncate">
                  {r.interpreter}
                </span>
                <span className="font-data text-[11.5px] text-ink/55">{timeAgo(r.at)}</span>
                <span
                  className="font-data text-[10.5px] text-ink/45 truncate"
                  title={r.evidenceHash}
                >
                  {r.evidenceHash}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ── TimeMachine — date picker that shifts the framework view ───────────────

export function TimeMachine({ asOf, onChange, max = '2026-05-19' }: TimeMachineProps) {
  const isLive = asOf === max;
  return (
    <div className="inline-flex items-center gap-2 h-10 px-3 rounded-[10px] border border-[#F3E4C8] bg-white">
      <Icon name="calendar" size={14} stroke="#121111" />
      <span className="font-data text-[12px] text-ink/55">As of</span>
      <input
        type="date"
        value={asOf}
        max={max}
        onChange={(e) => onChange(e.target.value)}
        className="font-display text-[13px] font-bold text-ink outline-none bg-transparent"
      />
      {!isLive && (
        <button
          type="button"
          onClick={() => onChange(max)}
          className="font-display text-[11px] font-bold text-[#FA500E] hover:underline ml-1"
        >
          back to live
        </button>
      )}
      {isLive ? (
        <Badge tone="ok" dot>
          live
        </Badge>
      ) : (
        <Badge tone="info" dot>
          frozen view
        </Badge>
      )}
    </div>
  );
}

// ── EvidencePacketModal — bundle generator ─────────────────────────────────

export function EvidencePacketModal({ open, onClose, framework, data, asOf }: EvidencePacketModalProps) {
  const [includeRuns, setIncludeRuns] = React.useState(true);
  const [includeRules, setIncludeRules] = React.useState(true);
  const [includeAudit, setIncludeAudit] = React.useState(true);
  const [signed, setSigned] = React.useState(true);
  const [step, setStep] = React.useState<'config' | 'done'>('config');
  
  React.useEffect(() => {
    if (open) {
      setStep('config');
      setIncludeRuns(true);
      setIncludeRules(true);
      setIncludeAudit(true);
      setSigned(true);
    }
  }, [open]);
  
  if (!framework) return null;

  const runsCount = data?.evidence?.runs.length || 0;
  const eventsCount = data?.auditLog?.events.length || 0;
  const rulesCount = (data?.conformance?.rules || []).filter((r: any) =>
    r.satisfies.some((s: any) => s.framework === framework.id)
  ).length;

  return (
    <ModalShell open={open} onClose={onClose} width={620}>
      {step === 'config' ? (
        <>
          <ModalHeader
            title="Export evidence packet"
            subtitle={`${framework.name} · as of ${asOf}`}
            onClose={onClose}
            icon="download"
          />
          <div className="px-6 py-5 flex flex-col gap-4">
            <p className="font-display text-[13px] text-ink/75">
              Bundles every control + interpreter + conformance rule + signed evidence run that
              proves your stance on <b>{framework.name}</b> at the point in time you've selected.
              Auditor-ready.
            </p>
            <ul className="flex flex-col gap-2">
              <PacketOption
                checked={includeRuns}
                onToggle={() => setIncludeRuns(!includeRuns)}
                title="Control evidence runs"
                sub={`${runsCount} hash-anchored runs across all controls in scope`}
              />
              <PacketOption
                checked={includeRules}
                onToggle={() => setIncludeRules(!includeRules)}
                title="Conformance rules"
                sub={`${rulesCount} rules satisfying ${framework.name} requirements`}
              />
              <PacketOption
                checked={includeAudit}
                onToggle={() => setIncludeAudit(!includeAudit)}
                title="Audit log entries"
                sub={`${eventsCount} events covering this period`}
              />
              <PacketOption
                checked={signed}
                onToggle={() => setSigned(!signed)}
                title="Sign with workspace key"
                sub="Auditors can verify the packet hasn't been tampered with"
              />
            </ul>
          </div>
          <div className="px-6 py-4 border-t border-[#F7EDDB] flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="h-10 px-4 rounded-[8px] border border-[#F3E4C8] font-display font-semibold text-[14px] hover:bg-[#FCF8F1]"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => setStep('done')}
              className="h-10 px-4 rounded-[8px] bg-[#FA500E] text-white font-display font-bold text-[14px] hover:opacity-90 whitespace-nowrap"
            >
              Generate packet
            </button>
          </div>
        </>
      ) : (
        <>
          <ModalHeader
            title="Evidence packet ready"
            subtitle={`${framework.name}-${asOf}.tar.gz · ${(Math.random() * 4 + 2).toFixed(1)} MB`}
            onClose={onClose}
            icon="check"
          />
          <div className="px-6 py-5 flex flex-col gap-4">
            <pre className="rounded-[8px] border border-[#F3E4C8] bg-[#121111] text-[#F7EDDB] p-4 font-data text-[12px] leading-[1.6]">
              {`packet/
├── manifest.json              (framework, asOf, scope, hash)
├── requirements/              (${framework.requirements.length} requirement files)
├── controls/                  (control definitions at point-in-time)
${includeRuns ? '├── evidence/                  (signed runs, append-only)\n' : ''}${includeRules ? '├── conformance.typeql         (rules + derived facts)\n' : ''}${includeAudit ? '├── audit-log.csv              (filtered to packet window)\n' : ''}└── signature.asc              ${signed ? '(workspace signing key)' : '(NOT signed)'}`}
            </pre>
            <div className="rounded-[8px] border border-[#C6F3D8] bg-[#C6F3D8]/30 p-4 flex items-center gap-3">
              <Icon name="check" size={16} stroke="#159D4C" />
              <span className="font-display text-[13px] text-ink">
                Cylift signed this packet. Auditors can verify with your workspace public key.
              </span>
            </div>
          </div>
          <div className="px-6 py-4 border-t border-[#F7EDDB] flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="h-10 px-4 rounded-[8px] border border-[#F3E4C8] font-display font-semibold text-[14px] hover:bg-[#FCF8F1]"
            >
              Close
            </button>
            <button
              type="button"
              className="h-10 px-4 rounded-[8px] bg-ink text-white font-display font-bold text-[14px] hover:opacity-90 inline-flex items-center gap-2"
            >
              <Icon name="download" size={13} stroke="white" /> Download .tar.gz
            </button>
          </div>
        </>
      )}
    </ModalShell>
  );
}

function PacketOption({ checked, onToggle, title, sub }: PacketOptionProps) {
  return (
    <li>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-start gap-3 px-3 py-2.5 rounded-[8px] hover:bg-[#FCF8F1] text-left"
      >
        <span
          className={cx(
            'h-4 w-4 rounded-[3px] border grid place-items-center mt-1 shrink-0',
            checked ? 'bg-[#FA500E] border-[#FA500E]' : 'border-ink/30'
          )}
        >
          {checked && <Icon name="check" size={10} stroke="white" />}
        </span>
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-ink text-[13px]">{title}</div>
          <div className="font-data text-[11.5px] text-ink/55 mt-0.5">{sub}</div>
        </div>
      </button>
    </li>
  );
}